import type { Column, RowData } from "@tanstack/react-table-v9";

import {
  ACTIONS_COLUMN_ID,
  EXPANDER_COLUMN_ID,
  SELECTION_COLUMN_ID,
} from "./columns";
import type { TableV2Features } from "./features";
import type { TableV2Instance } from "./hooks/useTableV2";

type AnyColumn<TData extends RowData> = Column<TableV2Features, TData, unknown>;

const UTILITY_COLUMN_IDS = new Set([
  SELECTION_COLUMN_ID,
  EXPANDER_COLUMN_ID,
  ACTIONS_COLUMN_ID,
]);

export const isUtilityColumn = <TData extends RowData>(
  column: AnyColumn<TData>,
) => UTILITY_COLUMN_IDS.has(column.id);

export const getColumnLabel = <TData extends RowData>(
  column: AnyColumn<TData>,
) => {
  const { header, meta } = column.columnDef;
  if (meta?.label) return meta.label;
  return typeof header === "string" ? header : column.id;
};

export const canReorderColumn = <TData extends RowData>(
  column: AnyColumn<TData>,
) => !isUtilityColumn(column) && column.columnDef.meta?.lockOrder !== true;

export const getLeafColumnOrder = <TData extends RowData>(
  table: TableV2Instance<TData>,
) => {
  const allIds = table.getAllLeafColumns().map((column) => column.id);
  const saved = table.state.columnOrder.filter((id) => allIds.includes(id));
  return [...saved, ...allIds.filter((id) => !saved.includes(id))];
};

export const moveColumn = <TData extends RowData>(
  table: TableV2Instance<TData>,
  fromId: string,
  toId: string,
  placement: "before" | "after" = "before",
) => {
  if (fromId === toId) return;
  const order = getLeafColumnOrder(table).filter((id) => id !== fromId);
  const targetIndex = order.indexOf(toId);
  if (targetIndex === -1) return;
  order.splice(
    placement === "after" ? targetIndex + 1 : targetIndex,
    0,
    fromId,
  );
  table.setColumnOrder(order);
};

export const nudgeColumn = <TData extends RowData>(
  table: TableV2Instance<TData>,
  columnId: string,
  direction: -1 | 1,
) => {
  const movable = getLeafColumnOrder(table).filter((id) => {
    const column = table.getColumn(id);
    // Stay within the same pinned region so every move is visible
    return column
      ? canReorderColumn(column) &&
          column.getIsPinned() === table.getColumn(columnId)?.getIsPinned()
      : false;
  });
  const index = movable.indexOf(columnId);
  const neighbor = movable[index + direction];
  if (index === -1 || !neighbor) return;
  moveColumn(table, columnId, neighbor, direction === 1 ? "after" : "before");
};

const CELL_CHROME = 22;

const measureContent = (element: Element): number => {
  if (!(element instanceof HTMLElement)) return 0;
  if (element.dataset.fitRow === undefined) return element.scrollWidth;
  // Header label: its text is clipped by a flex parent, so add up the children instead
  const children = Array.from(element.children);
  const gap = parseFloat(getComputedStyle(element).columnGap) || 0;
  return (
    children.reduce((total, child) => total + measureContent(child), 0) +
    gap * Math.max(children.length - 1, 0)
  );
};

export const fitColumnToContent = <TData extends RowData>(
  table: TableV2Instance<TData>,
  root: HTMLElement | null,
  columnId: string,
) => {
  const column = table.getColumn(columnId);
  if (!root || !column) return;
  const widest = Array.from(
    root.querySelectorAll<HTMLElement>("[data-column-id]"),
  )
    .filter((cell) => cell.dataset.columnId === columnId)
    .reduce((max, cell) => {
      const content = cell.firstElementChild;
      return content ? Math.max(max, measureContent(content)) : max;
    }, 0);
  if (!widest) return;
  const { minSize = 0, maxSize = Number.MAX_SAFE_INTEGER } = column.columnDef;
  const width = Math.min(
    Math.max(Math.ceil(widest + CELL_CHROME), minSize),
    maxSize,
  );
  table.setColumnSizing((sizes) => {
    return { ...sizes, [columnId]: width };
  });
};
